// /src/pages/SoporteAdministrativo.tsx
import React, { useState, useEffect, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { useAuth } from "../security/AuthLogin";
import {
  fetchSoporteMensajes,
  sendSoporteMensaje,
  fetchArchivosSoporte,
} from "../api/soportesApi";
import { getAuthToken } from "../security/AuthLogin";
import { io, Socket } from "socket.io-client";
import DOMPurify from "dompurify";
import "react-quill/dist/quill.snow.css";
import "../css/favorito.css";
import ChatComponent from "../components/ChatComponent";
import CalendlyButton from "../components/CalendlyButton";

// ---------------------
// INTERFACES
// ---------------------
interface SoporteAdminData {
  cliente_email: string;
  cliente_nombre: string;
  cliente_profilepic?: string | null;
  codigo_expediente: string;
  dueno_email?: string | null;
  dueno_id?: string | null;
  dueno_nombre?: string | null;
  enlace: string;
  estado_concurso: string;
  estado_soporte?: string | null;
  estado_soporte_favorito: string;
  id_concurso: string;
  id_soporte: number;
  id_usuario: string;
  leido: number;
  titulo: string;
  ultima_fecha_msg: string;
  ultimo_emisor: string;
}

interface MensajeSoporte {
  id_soporte_detalle: number;
  id_soporte: number;
  id_usuario: string;
  f_respuesta: string;
  hora_respuesta: string;
  texto: string;
  es_cliente: boolean;
}

interface ArchivoSoporte {
  path: string;
  uploaderRole: "cliente" | "admin" | "tecnico";
  dateFolder?: string | null;
}

interface TecnicoData {
  id_usuario: string;
  nombre: string;
  email: string;
  rol?: string;
}

const BASE_URL = `${window.location.protocol}//${window.location.hostname}`;

const mapEstado = (estado: string | null): string => {
  if (!estado) return "En estudio";
  switch (estado?.toLowerCase()) {
    case "en estudio":
      return "En estudio";
    case "valido":
      return "Viable";
    case "rechazado":
      return "No viable";
    case "oculto":
      return "Oculto";
    case "norecomendado":
      return "No recomendado";
    default:
      return estado.charAt(0).toUpperCase() + estado.slice(1);
  }
};

const SoporteAdministrativo: React.FC = () => {
  const { id_concurso, id_usuario } = useParams<{
    id_concurso: string;
    id_usuario: string;
  }>();
  const { userRole, userData, userId } = useAuth();

  const [loading, setLoading] = useState(false);
  const [soporteAdmin, setSoporteAdmin] = useState<SoporteAdminData | null>(
    null
  );
  const [, setMensajes] = useState<MensajeSoporte[]>([]);
  const [, setArchivos] = useState<ArchivoSoporte[]>([]);
  const [socket, setSocket] = useState<Socket | null>(null);
  const [, setAlguienTyping] = useState({
    isTyping: false,
    quien: "",
  });
  const typingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const messagesEndRef = useRef<HTMLDivElement | null>(null);

  const [personalActivo, setPersonalActivo] = useState<TecnicoData[]>([]);
  const [selectedTecnico, setSelectedTecnico] = useState("");
  const [showReassignModal, setShowReassignModal] = useState(false);

  // ★ NUEVO: estados para modal de confirmación
  const [confirmModalVisible, setConfirmModalVisible] = useState(false);
  const [confirmType, setConfirmType] = useState<
    "baja" | "tecnico" | "juridico" | null
  >(null);
  const [confirmMessage, setConfirmMessage] = useState("");
  const esCliente = userRole === "cliente";

  // ★ NUEVO: configuración tipos de soporte
  const tipoConfig = {
    baja: {
      label: "Baja estadística",
      endpoint: "/api/baja-estadistica",
      redirectPath: (c: string, u: string) => `/baja-estadistica/${c}/${u}`,
    },
    tecnico: {
      label: "Soporte técnico",
      endpoint: "/api/soporte-tecnico",
      redirectPath: (c: string, u: string) => `/soporte-tecnico/${c}/${u}`,
    },
    juridico: {
      label: "Soporte jurídico",
      endpoint: "/api/soporte-juridico",
      redirectPath: (c: string, u: string) => `/soporte-juridico/${c}/${u}`,
    },
  };

  // ★ NUEVO: comprueba existencia de soporte
  const checkSupportExists = async (type: keyof typeof tipoConfig) => {
    if (!soporteAdmin) return false;
    const token = getAuthToken();
    const resp = await fetch(
      `${BASE_URL}/api/soporte-existe?tipo=${type}&id_concurso=${soporteAdmin.id_concurso}&id_usuario=${soporteAdmin.id_usuario}`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    if (!resp.ok) return false;
    const json = await resp.json();
    return json.exists as boolean;
  };

  // ★ NUEVO: wrapper click soportes
  const handleButtonClick = async (type: keyof typeof tipoConfig) => {
    if (!soporteAdmin) return;
    const exists = await checkSupportExists(type);
    if (exists) {
      await invokeSupportHandler(type, "");
    } else {
      setConfirmType(type);
      setConfirmMessage("");
      setConfirmModalVisible(true);
    }
  };

  // ★ NUEVO: invoca endpoint, envía mensaje, redirige
  const invokeSupportHandler = async (
    type: keyof typeof tipoConfig,
    initialMessage: string
  ) => {
    if (!soporteAdmin) return;
    const { endpoint, redirectPath } = tipoConfig[type];
    try {
      const token = getAuthToken();
      const resp = await fetch(endpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          id_concurso: soporteAdmin.id_concurso,
          id_usuario: soporteAdmin.id_usuario,
        }),
      });
      if (!resp.ok) throw new Error((await resp.text()) || "");
      const data = await resp.json();
      if (data.success && data.id_soporte) {
        if (initialMessage.trim()) {
          await sendSoporteMensaje(
            data.id_soporte,
            DOMPurify.sanitize(initialMessage)
          );
        }
        window.location.href = redirectPath(
          soporteAdmin.id_concurso,
          soporteAdmin.id_usuario
        );
      }
    } catch (err: any) {
      console.error(err);
      alert(err.message);
    }
  };

  // ★ NUEVO: confirmar modal
  const handleConfirmYes = async () => {
    if (confirmType) {
      await invokeSupportHandler(confirmType, confirmMessage);
      setConfirmModalVisible(false);
      setConfirmType(null);
      setConfirmMessage("");
    }
  };
  const handleConfirmNo = () => {
    setConfirmModalVisible(false);
    setConfirmType(null);
    setConfirmMessage("");
  };

  // Socket
  useEffect(() => {
    const s = io(BASE_URL, {
      withCredentials: true,
      auth: (cb) => cb({ token: localStorage.getItem("authToken") }),
    });
    setSocket(s);
    return () => {
      s.disconnect();
    };
  }, []);

  // Cargar soporte administrativo
  useEffect(() => {
    if (id_concurso && id_usuario) {
      loadSoporteAdmin(id_concurso, id_usuario);
    }
  }, [id_concurso, id_usuario]);

  const loadSoporteAdmin = async (concursoId: string, userIdParam: string) => {
    setLoading(true);
    try {
      const token = getAuthToken();
      if (!token) throw new Error("No autenticado");
      const resp = await fetch(
        `${BASE_URL}/api/soportes-administrativos/detalle/${concursoId}/${userIdParam}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!resp.ok) {
        const txt = await resp.text();
        throw new Error(txt || "Error al cargar soporte administrativo");
      }
      const data = await resp.json();
      if (data.success && data.soporte_administrativo) {
        setSoporteAdmin(data.soporte_administrativo);
        if (data.soporte_administrativo.id_soporte) {
          await loadChat(data.soporte_administrativo.id_soporte);
          await loadArchivos(data.soporte_administrativo.id_soporte);
        }
      }
    } catch (error: any) {
      console.error("Error loadSoporteAdmin:", error);
    } finally {
      setLoading(false);
    }
  };

  const loadChat = async (idSoporte: number) => {
    try {
      const resp = await fetchSoporteMensajes(idSoporte);
      if (resp.success) {
        setMensajes(resp.messages);
        scrollToBottom();
      }
    } catch (error: any) {
      console.error("Error loading chat:", error);
    }
  };

  const loadArchivos = async (idSoporte: number) => {
    try {
      const resp = await fetchArchivosSoporte(idSoporte);
      if (resp.success) setArchivos(resp.archivos);
    } catch (error: any) {
      console.error("Error loading files:", error);
    }
  };

  useEffect(() => {
    if (socket && soporteAdmin?.id_soporte) {
      socket.emit("joinSoporte", soporteAdmin.id_soporte);
      socket.on("nuevoMensaje", (msg: MensajeSoporte) => {
        setMensajes((prev) => [...prev, msg]);
        scrollToBottom();
      });
      socket.on("alguienEstaEscribiendo", ({ isTyping, quien }) => {
        setAlguienTyping({ isTyping, quien });
        if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
        if (isTyping) {
          typingTimeoutRef.current = setTimeout(() => {
            setAlguienTyping({ isTyping: false, quien: "" });
          }, 5000);
        }
      });
    }
    return () => {
      if (socket) {
        socket.off("nuevoMensaje");
        socket.off("alguienEstaEscribiendo");
      }
    };
  }, [socket, soporteAdmin?.id_soporte]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    if (userRole === "admin" || userRole === "tecnico") {
      fetchPersonalActivo();
    }
  }, [userRole]);

  const fetchPersonalActivo = async () => {
    try {
      const token = getAuthToken();
      if (!token) return;
      const resp = await fetch(`${BASE_URL}/api/usuarios/admins-tecnicos`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const json = await resp.json();
      if (json.success) {
        setPersonalActivo(json.personal);
      }
    } catch (error) {
      console.error("Error fetching personal activo:", error);
    }
  };

  // Lógica de dueños
  const handleDueñoAction = async (
    action: "becomeOwner" | "dropOwner" | "assignTo",
    newDueno?: string
  ) => {
    if (!soporteAdmin?.id_soporte) return;
    try {
      const resp = await fetch(
        `${BASE_URL}/api/soportes/${soporteAdmin.id_soporte}/dueno`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${getAuthToken()}`,
          },
          body: JSON.stringify({ action, ...(newDueno && { newDueno }) }),
        }
      );
      if (!resp.ok) {
        const txt = await resp.text();
        throw new Error(`Error al actualizar dueño: ${txt}`);
      }
      const data = await resp.json();
      if (data.success) {
        await loadSoporteAdmin(
          soporteAdmin.id_concurso,
          soporteAdmin.id_usuario
        );
        alert(data.message);
      } else {
        alert(data.message || "Error al cambiar dueño");
      }
    } catch (error: any) {
      console.error("Error updating owner:", error);
      alert(`Error al cambiar dueño: ${error.message}`);
    }
  };

  const handleReassignModalOpen = () => {
    setShowReassignModal(true);
  };

  const handleReassignConfirm = async () => {
    if (!selectedTecnico) {
      alert("Selecciona un técnico");
      return;
    }
    await handleDueñoAction("assignTo", selectedTecnico);
    setShowReassignModal(false);
    setSelectedTecnico("");
  };

  if (loading) {
    return (
      <div className="concurso-container">
        <p>Cargando soporte administrativo...</p>
      </div>
    );
  }

  if (!soporteAdmin) {
    return (
      <div className="concurso-container">
        <p>No se encontró el soporte administrativo</p>
      </div>
    );
  }

  // ★ CORREGIDO: Comparar correctamente el ID del usuario actual
  const hayDueño = !!soporteAdmin.dueno_nombre;
  const soyDueño = userId === soporteAdmin.dueno_id;
  const esAdmin = userRole === "admin";
  const esTec = userRole === "tecnico";
  const esAdminOTec = esAdmin || esTec;

  return (
    <div className="favorito-page">
      <aside className="sticky-sidebar">
        {/* enlaces estáticos */}
        <button className="sidebar-button">
          <Link
            to={`/concurso/${soporteAdmin.id_concurso}`}
            style={{ color: "inherit", textDecoration: "none" }}
          >
            Ver concurso
          </Link>
        </button>
        <button className="sidebar-button">
          <Link
            to={`/favorito/${soporteAdmin.id_concurso}/${soporteAdmin.id_usuario}`}
            style={{ color: "inherit", textDecoration: "none" }}
          >
            Ver favorito
          </Link>
        </button>
        {/* botones con confirmación */}
        <button
          className="sidebar-button"
          onClick={() => handleButtonClick("baja")}
        >
          Baja estadística
        </button>
        <button
          className="sidebar-button"
          onClick={() => handleButtonClick("tecnico")}
        >
          Soporte técnico
        </button>
        <button
          className="sidebar-button"
          onClick={() => handleButtonClick("juridico")}
        >
          Soporte jurídico
        </button>
      </aside>

      <div className="concurso-container">
        <div
          className="concurso-header"
          style={{ display: "flex", justifyContent: "space-between" }}
        >
          <div>
            <h1>Soporte Administrativo</h1>
            <p>Expediente: {soporteAdmin.codigo_expediente}</p>
            {esAdminOTec &&
              (hayDueño ? (
                <p>
                  Responsable: {soporteAdmin.dueno_nombre} (
                  {soporteAdmin.dueno_email})
                </p>
              ) : (
                <p>Sin responsable asignado</p>
              ))}
          </div>
          {esAdminOTec && (
            <div style={{ display: "flex", alignItems: "center", gap: "1rem" }}>
              {!hayDueño && (
                <button
                  className="btn btn-secondary"
                  onClick={() => handleDueñoAction("becomeOwner")}
                >
                  Asignarme
                </button>
              )}

              {/* ★ CORREGIDO: Botón Liberar - Los admin siempre pueden liberar, los técnicos solo si son dueños */}
              {hayDueño && (esAdmin || soyDueño) && (
                <button
                  className="btn btn-secondary"
                  onClick={() => handleDueñoAction("dropOwner")}
                >
                  Liberar
                </button>
              )}

              {/* ★ CORREGIDO: Botón Reasignar - En soporte administrativo, tanto admin como técnico pueden reasignar */}
              {hayDueño && esAdminOTec && (
                <button
                  className="btn btn-secondary"
                  onClick={handleReassignModalOpen}
                >
                  Reasignar
                </button>
              )}
            </div>
          )}
        </div>

        {showReassignModal && (
          <div className="modal-overlay">
            <div className="modal-content">
              <h3>Reasignar soporte</h3>
              <select
                value={selectedTecnico}
                onChange={(e) => setSelectedTecnico(e.target.value)}
              >
                <option value="">Seleccionar técnico...</option>
                {personalActivo.map((t) => (
                  <option key={t.id_usuario} value={t.id_usuario}>
                    {t.nombre} ({t.email})
                  </option>
                ))}
              </select>
              <div style={{ marginTop: "1rem" }}>
                <button
                  onClick={handleReassignConfirm}
                  style={{ marginRight: "1rem" }}
                >
                  Confirmar
                </button>
                <button onClick={() => setShowReassignModal(false)}>
                  Cancelar
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="section">
          <h2>Detalles</h2>
          <div className="info-grid">
            <div className="field">
              <label>Título</label>
              <span>{soporteAdmin.titulo}</span>
            </div>
            <div className="enlaces-container">
              <label>Enlace</label>
              {soporteAdmin.enlace ? (
                <a
                  href={soporteAdmin.enlace}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="enlace-oficial"
                >
                  Enlace oficial
                </a>
              ) : (
                <span>No disponible</span>
              )}

              {/* ★ Botón de Calendly solo para clientes cuando hay responsable asignado */}
              {esCliente && hayDueño && (
                <div style={{ marginTop: "1rem" }}>
                  <CalendlyButton
                    dueno_email={soporteAdmin.dueno_email}
                    dueno_nombre={soporteAdmin.dueno_nombre}
                    soporteData={{
                      id_concurso: soporteAdmin.id_concurso,
                      id_usuario: soporteAdmin.id_usuario,
                      titulo: soporteAdmin.titulo,
                      codigo_expediente: soporteAdmin.codigo_expediente,
                      cliente_nombre: soporteAdmin.cliente_nombre,
                      cliente_email: soporteAdmin.cliente_email,
                    }}
                    buttonText="📞 Agendar reunión"
                    variant="primary"
                  />
                </div>
              )}
            </div>
          </div>
          <div className="estado-container" style={{ marginTop: "1rem" }}>
            <label>
              <strong>Estado del soporte:</strong>
            </label>{" "}
            <span>{mapEstado(soporteAdmin.estado_soporte_favorito)}</span>
          </div>
        </div>

        <div className="section">
          <h2>Comunicación</h2>
          <div className="section">
            <ChatComponent
              idSoporte={soporteAdmin.id_soporte}
              clienteNombre={soporteAdmin.cliente_nombre}
              clienteEmail={soporteAdmin.cliente_email}
              clienteProfilePic={soporteAdmin.cliente_profilepic}
              sendText={true}
              allowFileUpload={true}
              showTypingIndicator={true}
            />
          </div>
        </div>
      </div>
      {confirmModalVisible && confirmType && (
        <div className="modal-overlay-soportes">
          <div className="modal-content-soportes">
            <h3>
              ¿Quieres solicitar el{" "}
              <strong>{tipoConfig[confirmType].label}</strong>?
            </h3>
            <textarea
              value={confirmMessage}
              onChange={(e) => setConfirmMessage(e.target.value)}
              placeholder="Describe tu solicitud…"
              rows={4}
              style={{ width: "100%", margin: "1rem 0" }}
            />
            <div className="modal-buttons-soportes">
              <button
                onClick={handleConfirmYes}
                disabled={!confirmMessage.trim()}
              >
                Sí
              </button>
              <button onClick={handleConfirmNo}>No</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SoporteAdministrativo;
